import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Seo from '@/components/feature/Seo';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/db933cc6oi3g3tv8us40';

const contentScope = [
  { icon: 'ri-book-read-line', title: '剧情介绍', desc: '整理每部剧的主线故事与分集梗概，帮你在追剧前快速了解故事走向。' },
  { icon: 'ri-user-star-line', title: '角色解析', desc: '梳理角色关系与人物弧光，深入了解每一个人物背后的动机与成长。' },
  { icon: 'ri-newspaper-line', title: '影视资讯', desc: '汇集行业动态、剧集资讯与幕后花絮，及时掌握影视圈的最新变化。' },
  { icon: 'ri-layout-grid-line', title: '分类检索', desc: '按类型、年代与地区多维筛选，配合站内搜索快速定位目标剧集。' },
];

export default function About() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'submitting') return;
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honey = String(formData.get('website_alt') ?? '').trim();
    if (honey) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    setStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value === 'string' && value.trim() !== '') payload.append(key, value);
    });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');
      if (!response.ok || parsed?.code !== 'OK' || isSpam) {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试。');
        return;
      }
      setStatus('success');
      setFormError('');
      form.reset();
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后再试。');
    }
  };

  return (
    <div className="w-full">
      <Seo
        title="关于我们 - 上司的老婆影视剧内容站"
        description="了解「上司的老婆」影视剧内容站的定位、内容范围与联系方式。我们专注电视剧剧情介绍、角色解析与影视行业动态。"
        keywords="关于我们,影视剧内容站,剧情介绍,角色解析,联系方式"
      />

      <section className="bg-primary-950 text-background-50">
        <div className="px-4 md:px-6 lg:px-10 py-12 md:py-16">
          <nav className="flex items-center gap-1.5 text-xs text-background-300 mb-4">
            <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-100">关于我们</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-4xl font-black">关于我们</h1>
          <p className="text-sm md:text-base text-background-300 mt-3 max-w-2xl leading-relaxed">
            我们是一个专注电视剧内容的资讯平台，希望用结构化、可检索的方式，让每一部好剧的故事都被认真讲述。
          </p>
        </div>
      </section>

      <section className="px-4 md:px-6 lg:px-10 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-8 lg:gap-12 items-start">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">网站简介</h2>
            <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-loose">
              <p>
                「上司的老婆」是一个面向剧迷与影视爱好者的电视剧内容站。我们聚焦于<strong className="text-foreground-900">剧情介绍、角色解析与影视行业动态</strong>，
                通过清晰的信息分层，帮助用户在最短时间内了解一部剧的核心内容。
              </p>
              <p>
                无论你是想在开播前快速了解剧情梗概，还是想在追剧后深挖角色关系与细节伏笔，
                都可以通过分类筛选、站内搜索与角色档案快速找到答案。我们也持续整理行业新闻，让你不错过影视圈的每一次变化。
              </p>
              <p>
                站内所有剧集、角色与资讯内容均为演示数据，用于展示平台的内容结构与浏览体验，不代表任何真实作品。
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
              <div className="rounded-lg bg-background-100 p-4">
                <div className="font-heading text-2xl font-black text-primary-600">1200+</div>
                <div className="text-xs text-foreground-500 mt-1">收录剧集</div>
              </div>
              <div className="rounded-lg bg-background-100 p-4">
                <div className="font-heading text-2xl font-black text-primary-600">8600+</div>
                <div className="text-xs text-foreground-500 mt-1">角色档案</div>
              </div>
              <div className="rounded-lg bg-background-100 p-4">
                <div className="font-heading text-2xl font-black text-primary-600">8</div>
                <div className="text-xs text-foreground-500 mt-1">剧集分类</div>
              </div>
              <div className="rounded-lg bg-background-100 p-4">
                <div className="font-heading text-2xl font-black text-accent-600">每日</div>
                <div className="text-xs text-foreground-500 mt-1">资讯更新</div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-secondary-100 bg-secondary-50 p-5 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-contacts-line text-accent-500"></i>
              联系方式
            </h2>
            <ul className="space-y-3.5 text-sm">
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-md bg-background-50 flex items-center justify-center text-primary-500">
                  <i className="ri-mail-line"></i>
                </span>
                <span className="text-foreground-700">contact@shangsideiaopo.com</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-md bg-background-50 flex items-center justify-center text-primary-500">
                  <i className="ri-wechat-line"></i>
                </span>
                <span className="text-foreground-700">微信公众号：上司的老婆影视站</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-md bg-background-50 flex items-center justify-center text-primary-500">
                  <i className="ri-time-line"></i>
                </span>
                <span className="text-foreground-700">工作日 09:00 - 18:00</span>
              </li>
            </ul>
            <p className="text-xs text-foreground-500 mt-5 leading-relaxed">
              内容合作、纠错建议或版权事宜，欢迎通过左侧表单留言，我们会在收到后尽快回复。
            </p>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 lg:px-10 pb-10 md:pb-14">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-5">内容范围与使用说明</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {contentScope.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center text-xl mb-4">
                <i className={item.icon}></i>
              </span>
              <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
              <p className="text-xs text-foreground-500 mt-2 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 md:px-6 lg:px-10 pb-14 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-12 items-start">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3 flex items-center gap-2">
              <i className="ri-copyright-line text-primary-500"></i>
              版权与免责声明
            </h2>
            <div className="space-y-3 text-sm text-foreground-600 leading-relaxed">
              <p>本站所有剧集名称、角色资料与资讯内容均为演示用途的示例数据，不指向任何真实作品、机构或人物。</p>
              <p>站内展示的图片为平台生成的示范图片，仅用于页面效果演示。如有内容涉及侵权，请通过上方联系方式与我们联系。</p>
              <p>收藏功能的数据保存在你的浏览器本地，仅用于演示，不会上传至服务器。留言表单仅收集你主动提交的信息。</p>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-chat-3-line text-accent-500"></i>
              给我们留言
            </h2>

            {status === 'success' ? (
              <div className="rounded-md bg-accent-50 border border-accent-200 p-4 mb-4 flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-accent-600 text-lg mt-0.5"></i>
                <div>
                  <p className="text-sm font-medium text-foreground-900">感谢你的留言！</p>
                  <p className="text-xs text-foreground-600 mt-1">我们已经收到你的信息，会尽快通过邮件回复你。</p>
                </div>
              </div>
            ) : null}

            {status === 'error' ? (
              <div className="rounded-md bg-background-100 border border-background-300 p-4 mb-4 flex items-start gap-3">
                <i className="ri-error-warning-fill text-accent-600 text-lg mt-0.5"></i>
                <p className="text-xs text-foreground-700 leading-relaxed">{formError || '提交失败，请稍后再试。'}</p>
              </div>
            ) : null}

            <form id="about-contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-xs text-foreground-500 mb-1.5">称呼</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="w-full px-3.5 py-2.5 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs text-foreground-500 mb-1.5">邮箱</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="w-full px-3.5 py-2.5 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-topic" className="block text-xs text-foreground-500 mb-1.5">留言类型</label>
                <select
                  id="contact-topic"
                  name="topic"
                  className="w-full px-3.5 py-2.5 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
                >
                  <option value="内容纠错">内容纠错</option>
                  <option value="内容合作">内容合作</option>
                  <option value="版权事宜">版权事宜</option>
                  <option value="产品建议">产品建议</option>
                </select>
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-xs text-foreground-500 mb-1.5">留言内容</label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请描述你的问题或建议（最多 500 字）"
                  className="w-full px-3.5 py-2.5 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
                ></textarea>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-extra-note"
              />

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
                {status === 'submitting' ? '提交中…' : '提交留言'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}